import React, { useEffect, useMemo, useState } from "react";
import { ChevronUpIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "./icons";

const PAGE_SIZE = 20;

const COLUMNS = [
  { field: "trip_date", label: "Date" },
  { field: "driver_name", label: "Driver" },
  { field: "sr_name", label: "SR name" },
  { field: "miles", label: "Miles" },
  { field: "status", label: "Status" },
  { field: "rate", label: "Rate" },
];

const STATUS_TONE = {
  Completed: "success",
  NoShow: "warning",
};

const compareValues = (a, b, field) => {
  if (field === "miles" || field === "rate") {
    return (parseFloat(a[field]) || 0) - (parseFloat(b[field]) || 0);
  }
  if (field === "trip_date") {
    const dateA = new Date(a.trip_date);
    const dateB = new Date(b.trip_date);
    if (!isNaN(dateA) && !isNaN(dateB)) return dateA - dateB;
  }
  return String(a[field] ?? "").localeCompare(String(b[field] ?? ""));
};

const TripTable = ({ trips, hasSearched }) => {
  const [sortField, setSortField] = useState("trip_date");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [trips]);

  const sortedTrips = useMemo(
    () =>
      [...trips].sort((a, b) => {
        const result = compareValues(a, b, sortField);
        return sortDirection === "asc" ? result : -result;
      }),
    [trips, sortField, sortDirection]
  );

  if (!hasSearched) {
    return (
      <p className="empty-state">
        Enter search criteria above and click Search to view trip results.
      </p>
    );
  }

  if (trips.length === 0) {
    return <p className="empty-state">No trips match your search criteria.</p>;
  }

  const totalPages = Math.max(1, Math.ceil(sortedTrips.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageTrips = sortedTrips.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  return (
    <div className="results-card">
      <div className="table-scroll">
        <table className="results-table">
          <thead>
            <tr>
              {COLUMNS.map(({ field, label }) => (
                <th key={field} onClick={() => handleSort(field)}>
                  <span className="th-inner">
                    {label}
                    {sortField === field &&
                      (sortDirection === "asc" ? <ChevronUpIcon /> : <ChevronDownIcon />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageTrips.map((trip, index) => (
              <tr key={index}>
                <td>{trip.trip_date}</td>
                <td>{trip.driver_name}</td>
                <td className="text-muted">{trip.sr_name}</td>
                <td className="text-right">{parseFloat(trip.miles) || 0}</td>
                <td>
                  <span className={`badge badge-${STATUS_TONE[trip.status] || "neutral"}`}>
                    {trip.status === "NoShow" ? "No show" : trip.status}
                  </span>
                </td>
                <td className="text-right text-strong">${(trip.rate || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination-bar">
          <button
            type="button"
            className="page-btn"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={safePage <= 1}
            aria-label="Previous page"
          >
            <ChevronLeftIcon />
          </button>
          <span className="page-status">
            Page {safePage} of {totalPages}
          </span>
          <button
            type="button"
            className="page-btn"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={safePage >= totalPages}
            aria-label="Next page"
          >
            <ChevronRightIcon />
          </button>
        </div>
      )}
    </div>
  );
};

export default TripTable;
